/**
 * Client check-in (/resources/client-check-in): attaches a reCAPTCHA v3 token
 * to the plain <form> POST before it is sent. The server rejects a check-in
 * with no token when reCAPTCHA is configured (RecaptchaService), so without a
 * site key the form submits normally.
 *
 * Contract: bundled into resources.js for every resources page, so it no-ops
 * when the form is absent and binds its handler once.
 */

function injectRecaptchaScript(siteKey) {
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    document.head.appendChild(script);
}

/** Resolves to a token, or null when reCAPTCHA did not load in time. */
async function getRecaptchaToken(siteKey) {
    if (typeof window.grecaptcha === 'undefined') {
        const loaded = await new Promise((resolve) => {
            const started = Date.now();
            const poll = setInterval(() => {
                if (typeof window.grecaptcha !== 'undefined') { clearInterval(poll); resolve(true); }
                else if (Date.now() - started > 8000) { clearInterval(poll); resolve(false); }
            }, 50);
        });
        if (!loaded) return null;
    }
    try {
        return await new Promise((resolve, reject) => {
            window.grecaptcha.ready(() => {
                window.grecaptcha.execute(siteKey, { action: 'client_check_in' }).then(resolve).catch(reject);
            });
        });
    } catch {
        return null;
    }
}

function initClientCheckIn() {
    const form = document.querySelector('.client-check-in__form');
    if (!form || form.dataset.checkInInitialized === 'true') return;
    form.dataset.checkInInitialized = 'true';

    const siteKey = form.dataset.recaptchaSiteKey || '';
    if (!siteKey) return;
    injectRecaptchaScript(siteKey);

    let sending = false;
    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (sending) return;
        sending = true;
        const button = form.querySelector('button[type="submit"]');
        if (button) button.disabled = true;

        const token = await getRecaptchaToken(siteKey);
        const field = form.querySelector('input[name="recaptchaToken"]');
        if (field) field.value = token || '';
        // A missing token still submits: the server answers with a message
        // telling the client to reload or tell the front desk.
        form.submit();
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initClientCheckIn);
} else {
    initClientCheckIn();
}
